import { create } from 'zustand'

import {
  getSessionAttendees as pbGetSessionAttendees,
  getMyAttendees as pbGetMyAttendees,
  getAllAttendees as pbGetAllAttendees,
  createAttendee as pbCreateAttendee,
  updateAttendee as pbUpdateAttendee,
  deleteAttendee as pbDeleteAttendee,
} from '../api/attendeesService'

import { useCourseStore } from './courseStore'
import { getCourseQuestions } from './constants'
import { useSessionStore } from './sessionStore'
import type {
  SessionAttendee,
  Student,
  AttendeeStatus,
  AttendeeSource,
} from './types'

interface AttendeeState {
  students: Student[]
  attendees: SessionAttendee[]

  loadSessionAttendees: (sessionId: string) => Promise<void>
  loadMyAttendees: () => Promise<void>
  loadAllAttendees: () => Promise<void>
  registerStudent: (
    sessionId: string,
    studentData: Omit<Student, 'id'>
  ) => Promise<{ student: Student; attendee: SessionAttendee }>
  submitQuiz: (attendeeId: string, answers: Record<string, number>) => Promise<number>
  updateAttendeeContact: (
    attendeeId: string,
    data: { firstName: string; lastName: string; phone: string; company: string }
  ) => Promise<void>
  markAttendeeCompleted: (attendeeId: string) => Promise<void>
  deleteAttendee: (attendeeId: string) => Promise<void>
  handleRealtimeAttendee: (action: 'create' | 'update' | 'delete', record: Record<string, unknown>) => void
  getSessionAttendees: (sessionId: string) => Array<SessionAttendee & { student: Student }>

  /** Internal helper: bulk-update attendee status without API call (state only). */
  _setAttendeeStatuses: (
    sessionId: string,
    fromStatus: AttendeeStatus,
    toStatus: AttendeeStatus
  ) => void
}

export const useAttendeeStore = create<AttendeeState>((set, get) => ({
  students: [],
  attendees: [],

  loadSessionAttendees: async (sessionId) => {
    try {
      const pbAttendees = await pbGetSessionAttendees(sessionId)
      const newAttendees: SessionAttendee[] = pbAttendees.map((a) => ({
        id: a.id,
        sessionId: a.sessionId,
        studentId: a.studentId,
        registeredAt: a.registeredAt,
        quizAnswers: a.quizAnswers ?? {},
        quizScore: a.quizScore ?? null,
        status: a.status,
        source: a.source ?? 'manual',
        order: a.order ?? null,
      }))
      set((s) => {
        const others = s.attendees.filter((a) => a.sessionId !== sessionId)

        const studentsToAdd: Student[] = []
        const studentsToUpdate: Map<string, Student> = new Map()

        for (const a of pbAttendees) {
          const emailLocal = a.studentId.split('@')[0] ?? a.studentId
          const student: Student = {
            id: a.studentId,
            firstName: a.firstName?.trim() || emailLocal,
            lastName: a.lastName?.trim() || '',
            email: a.studentId,
            phone: a.phone?.trim() || '',
            company: a.company?.trim() || '',
          }
          if (s.students.some((st) => st.id === a.studentId)) {
            studentsToUpdate.set(a.studentId, student)
          } else {
            studentsToAdd.push(student)
          }
        }

        const mergedStudents = [
          ...s.students.map((st) => studentsToUpdate.get(st.id) ?? st),
          ...studentsToAdd,
        ]

        return {
          attendees: [...others, ...newAttendees],
          students: mergedStudents,
        }
      })
    } catch (err) {
      console.error('[AttendeeStore] loadSessionAttendees failed:', err)
      throw err
    }
  },

  loadMyAttendees: async () => {
    try {
      const pbAttendees = await pbGetMyAttendees()
      const newAttendees: SessionAttendee[] = pbAttendees.map((a) => ({
        id: a.id,
        sessionId: a.sessionId,
        studentId: a.studentId,
        registeredAt: a.registeredAt,
        quizAnswers: a.quizAnswers ?? {},
        quizScore: a.quizScore ?? null,
        status: a.status,
        source: a.source ?? 'manual',
        order: a.order ?? null,
      }))
      set((s) => {
        const ids = new Set(newAttendees.map((a) => a.id))
        const others = s.attendees.filter((a) => !ids.has(a.id))

        const studentsToAdd: Student[] = []
        const studentsToUpdate: Map<string, Student> = new Map()

        for (const a of pbAttendees) {
          const emailLocal = a.studentId.split('@')[0] ?? a.studentId
          const student: Student = {
            id: a.studentId,
            firstName: a.firstName?.trim() || emailLocal,
            lastName: a.lastName?.trim() || '',
            email: a.studentId,
            phone: a.phone?.trim() || '',
            company: a.company?.trim() || '',
          }
          if (s.students.some((st) => st.id === a.studentId)) {
            studentsToUpdate.set(a.studentId, student)
          } else {
            studentsToAdd.push(student)
          }
        }

        const mergedStudents = [
          ...s.students.map((st) => studentsToUpdate.get(st.id) ?? st),
          ...studentsToAdd,
        ]

        return {
          attendees: [...others, ...newAttendees],
          students: mergedStudents,
        }
      })
    } catch (err) {
      console.error('[AttendeeStore] loadMyAttendees failed:', err)
    }
  },

  loadAllAttendees: async () => {
    try {
      const pbAttendees = await pbGetAllAttendees()
      const newAttendees: SessionAttendee[] = pbAttendees.map((a) => ({
        id: a.id,
        sessionId: a.sessionId,
        studentId: a.studentId,
        registeredAt: a.registeredAt,
        quizAnswers: a.quizAnswers ?? {},
        quizScore: a.quizScore ?? null,
        status: a.status,
        source: a.source ?? 'manual',
        order: a.order ?? null,
      }))
      set((s) => {
        const ids = new Set(newAttendees.map((a) => a.id))
        const others = s.attendees.filter((a) => !ids.has(a.id))

        const studentsToAdd: Student[] = []
        const studentsToUpdate: Map<string, Student> = new Map()

        for (const a of pbAttendees) {
          const emailLocal = a.studentId.split('@')[0] ?? a.studentId
          const student: Student = {
            id: a.studentId,
            firstName: a.firstName?.trim() || emailLocal,
            lastName: a.lastName?.trim() || '',
            email: a.studentId,
            phone: a.phone?.trim() || '',
            company: a.company?.trim() || '',
          }
          if (s.students.some((st) => st.id === a.studentId)) {
            studentsToUpdate.set(a.studentId, student)
          } else {
            studentsToAdd.push(student)
          }
        }

        const mergedStudents = [
          ...s.students.map((st) => studentsToUpdate.get(st.id) ?? st),
          ...studentsToAdd,
        ]

        return {
          attendees: [...others, ...newAttendees],
          students: mergedStudents,
        }
      })
    } catch (err) {
      console.error('[AttendeeStore] loadAllAttendees failed:', err)
    }
  },

  registerStudent: async (sessionId, studentData) => {
    const state = get()
    const studentId = studentData.email.toLowerCase().trim()

    let student: Student | undefined = state.students.find((s) => s.id === studentId)
    if (!student) {
      student = { id: studentId, ...studentData }
      set((s) => ({ students: [...s.students, student!] }))
    }

    const now = new Date().toISOString()
    const pbAttendee = await pbCreateAttendee({
      sessionId,
      studentId,
      registeredAt: now,
      status: 'registered' as AttendeeStatus,
      firstName: studentData.firstName,
      lastName: studentData.lastName,
      phone: studentData.phone,
      company: studentData.company,
    })

    const attendee: SessionAttendee = {
      id: pbAttendee.id,
      sessionId,
      studentId,
      registeredAt: now,
      quizAnswers: {},
      quizScore: null,
      status: 'registered',
      source: pbAttendee.source ?? 'manual',
      order: pbAttendee.order ?? null,
    }
    set((s) => ({ attendees: [...s.attendees, attendee] }))
    return { student, attendee }
  },

  submitQuiz: async (attendeeId, answers) => {
    const state = get()
    const attendee = state.attendees.find((a) => a.id === attendeeId)
    if (!attendee) {return 0}

    const sessions = useSessionStore.getState().sessions
    const session = sessions.find((s) => s.id === attendee.sessionId)
    if (!session) {return 0}

    const courses = useCourseStore.getState().courses
    const course = courses.find((c) => c.slug === session.courseId)
    if (!course) {return 0}

    const questions = getCourseQuestions(course)
    if (questions.length === 0) {return 0}

    const correct = questions.filter(
      (q) => answers[q.id] === q.correctIndex
    ).length
    const score = Math.round((correct / questions.length) * 100)

    await pbUpdateAttendee(attendeeId, {
      quizAnswers: answers,
      quizScore: score,
      status: 'quiz-completed' as AttendeeStatus,
    })

    set((s) => ({
      attendees: s.attendees.map((a) =>
        a.id === attendeeId
          ? { ...a, quizAnswers: answers, quizScore: score, status: 'quiz-completed' as AttendeeStatus }
          : a
      ),
    }))
    return score
  },

  updateAttendeeContact: async (attendeeId, data) => {
    await pbUpdateAttendee(attendeeId, data)
    const attendee = get().attendees.find((a) => a.id === attendeeId)
    if (!attendee) {return}
    set((s) => ({
      students: s.students.map((st) =>
        st.id === attendee.studentId
          ? { ...st, firstName: data.firstName, lastName: data.lastName, phone: data.phone, company: data.company }
          : st
      ),
    }))
  },

  markAttendeeCompleted: async (attendeeId) => {
    await pbUpdateAttendee(attendeeId, { status: 'completed' as AttendeeStatus })
    set((s) => ({
      attendees: s.attendees.map((a) =>
        a.id === attendeeId ? { ...a, status: 'completed' as AttendeeStatus } : a
      ),
    }))
  },

  deleteAttendee: async (attendeeId) => {
    await pbDeleteAttendee(attendeeId)
    set((s) => ({
      attendees: s.attendees.filter((a) => a.id !== attendeeId),
    }))
  },

  handleRealtimeAttendee: (action, record) => {
    const id = record.id as string
    if (!id) {return}

    if (action === 'delete') {
      set((s) => ({
        attendees: s.attendees.filter((a) => a.id !== id),
      }))
      return
    }

    const sessionId = (record.session_id as string) || (record.sessionId as string) || (record.session as string) || ''
    const studentId = (record.student_email as string) || (record.studentId as string) || ''
    const attendee: SessionAttendee = {
      id,
      sessionId,
      studentId,
      registeredAt: (record.registered_at as string) || (record.registeredAt as string) || new Date().toISOString(),
      quizAnswers: (record.quiz_answers as Record<string, number>) || (record.quizAnswers as Record<string, number>) || {},
      quizScore: (record.quiz_score as number | null) ?? (record.quizScore as number | null) ?? null,
      status: (record.status as AttendeeStatus) || 'registered',
      source: (record.source as AttendeeSource) || 'manual',
      order: (record.order as string | null) ?? (record.order_id as string | null) ?? null,
    }

    const firstName = (record.first_name as string) || (record.firstName as string) || ''
    const lastName = (record.last_name as string) || (record.lastName as string) || ''
    const phone = (record.phone as string) || ''
    const company = (record.company as string) || ''
    if (studentId && firstName) {
      set((s) => {
        const exists = s.students.some((st) => st.id === studentId)
        const students = exists
          ? s.students.map((st) =>
              st.id === studentId ? { ...st, firstName, lastName, phone, company } : st
            )
          : [...s.students, { id: studentId, firstName, lastName, email: studentId, phone, company }]
        return { students }
      })
    }

    if (action === 'create') {
      set((s) => {
        if (s.attendees.some((a) => a.id === id)) {return s}
        return { attendees: [...s.attendees, attendee] }
      })
    } else if (action === 'update') {
      set((s) => ({
        attendees: s.attendees.map((a) => (a.id === id ? attendee : a)),
      }))
    }
  },

  getSessionAttendees: (sessionId) => {
    const { attendees, students } = get()
    return attendees
      .filter((a) => a.sessionId === sessionId)
      .map((a) => ({ ...a, student: students.find((s) => s.id === a.studentId)! }))
      .filter((a) => a.student)
  },

  _setAttendeeStatuses: (sessionId, fromStatus, toStatus) => {
    set((s) => ({
      attendees: s.attendees.map((a) =>
        a.sessionId === sessionId && a.status === fromStatus
          ? { ...a, status: toStatus }
          : a
      ),
    }))
  },
}))
